(ns practitest.frontend.integration-settings.clickup-form
  (:require [practitest.frontend.init :as init]
            [practitest.frontend.common.utils :as utils]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [re-frame.core :as re-frame]
            [practitest.frontend.integration-settings.entity-mapping.events :as events]
            [practitest.frontend.integration-settings.entity-mapping.subs :as subs]
            [practitest.frontend.integration-settings.shared-form :as shared-form]
            [practitest.frontend.components.selector2 :as selector2]
            [practitest.frontend.components2.spinners :as spinners]))


(defn- get-available-type-options
  [task-types current-mappings list-id entity-type other-entity-enabled?]
  (if-not task-types
    []
    (let [other-entity-type    (shared-form/get-other-entity-type entity-type)
          other-entity-map     (get-in current-mappings [(keyword list-id) other-entity-type])
          other-is-disabled?   (:disabled other-entity-map false)
          other-selected-types (if (or other-is-disabled? (not other-entity-enabled?))
                                 #{}
                                 (set (:ex_type other-entity-map [])))]
      (vec (remove #(contains? other-selected-types (:id %))
                   (map (fn [t] {:id (:id t) :label (:name t)}) task-types))))))


(defn- list-mapping-item
  [{:keys [project current-mappings entity-type other-entity-enabled?]}]
  (let [list-id        (name (:id project))
        space-id       (:space_id project)
        all-types      @(re-frame/subscribe [::subs/project-work-item-types])
        task-types     (get all-types space-id)
        types-loading? @(re-frame/subscribe [::subs/project-types-loading? space-id])
        entity-label   (shared-form/get-entity-label entity-type)
        mapping        (get current-mappings (keyword list-id))
        entity-map     (get mapping entity-type)
        is-disabled?   (:disabled entity-map false)
        type-options   (get-available-type-options task-types current-mappings list-id
                                                    entity-type other-entity-enabled?)
        handle-change  (fn [types]
                         (mixpanel/track "ClickUp Integration Settings"
                                         {"Action" (str "Changed " entity-label " task types")})
                         (re-frame/dispatch [::events/update-project-mapping list-id entity-type :ex_type types]))
        handle-toggle  (fn []
                         (mixpanel/track "ClickUp Integration Settings"
                                         {"Action" (str (if is-disabled? "Enable" "Disable")
                                                        " " entity-label " synchronization")})
                         (re-frame/dispatch [::events/toggle-project-entity-status list-id entity-type]))]
    [shared-form/project-mapping-row
     {:project-name    (:label project)
      :disabled?       is-disabled?
      :types-loading?  types-loading?
      :selected-types  (:ex_type entity-map [])
      :pt-entity       (shared-form/get-pt-entity-label entity-type)
      :type-options    type-options
      :placeholder     (format-translation (get-translation "bugtracker.integration-settings.select-item-types.placeholder"
                                                            "Select %{bugtracker} types")
                                           :bugtracker "ClickUp")
      :on-types-change handle-change
      :on-toggle       handle-toggle}]))


(defn- space-list-selector
  [{:keys [available-spaces selected-space-ids spaces-loading?
           available-lists-by-space selected-list-ids lists-loading?]}]
  (let [available-lists (->> selected-space-ids
                             (mapcat (fn [sid] (get available-lists-by-space (keyword sid) [])))
                             vec)
        space-value     (set (filter #(contains? selected-space-ids (:id %)) available-spaces))]
    [:div.form-group.clickup-space-list-selector
     [:div.clickup-selector-col
      [:label (get-translation "bugtracker.integration-settings.clickup.select-spaces"
                               "Select ClickUp Spaces to Sync") ":"]
      (if spaces-loading?
        [spinners/mux-spinner {:size :small}]
        [selector2/new-select
         {:id          "space-selector"
          :multi?      true
          :tags?       false
          :strategy    "fixed"
          :value       space-value
          :items       available-spaces
          :key-fn      :id
          :label-fn    :name
          :placeholder (str (get-translation "bugtracker.integration-settings.clickup.select-spaces.placeholder"
                                             "Select spaces") "...")
          :on-change   (fn [selected-set]
                         (let [selected-ids (mapv :id selected-set)]
                           (mixpanel/track "ClickUp Integration Settings" {"Action" "Selected ClickUp Spaces"})
                           (re-frame/dispatch [::events/update-selected-spaces selected-ids])))}])]
     [:div.clickup-selector-col
      [:label (get-translation "bugtracker.integration-settings.clickup.select-lists"
                               "Select ClickUp Lists to Sync") ":"]
      (if lists-loading?
        [spinners/mux-spinner {:size :small :inside? false}]
        [:div {:class (when (empty? selected-space-ids) "selector-disabled")}
         [selector2/new-select
          {:id          "list-selector"
           :multi?      true
           :tags?       false
           :strategy    "fixed"
          :value       (set (filter #(contains? selected-list-ids (keyword (:id %))) available-lists))
          :items       available-lists
          :key-fn      :id
          :label-fn    :label
          :placeholder (str (get-translation "bugtracker.integration-settings.clickup.select-lists.placeholder"
                                             "Select lists") "...")
          :on-change   (fn [selected-set]
                         (let [selected-ids (mapv (comp keyword :id) selected-set)]
                           (mixpanel/track "ClickUp Integration Settings" {"Action" "Selected ClickUp Lists"})
                           (re-frame/dispatch [::events/update-selected-lists selected-ids])))}]])]]))


(defn- build-projects-for-section
  [selected-list-ids available-lists-by-space configured-lists available-spaces]
  (let [space-names   (into {} (map (fn [s] [(:id s) (:name s)]) available-spaces))
        all-available (->> (vals available-lists-by-space)
                           (apply concat)
                           (into {} (map (fn [l] [(:id l) l]))))]
    (->> selected-list-ids
         (keep (fn [lid]
                 (let [lid-str (name lid)]
                   (or (when-let [l (get all-available lid-str)]
                         (let [sid (some (fn [[sid lists]]
                                           (when (some #(= (:id %) lid-str) lists)
                                             (name sid)))
                                         available-lists-by-space)
                               sname (get space-names sid)]
                           {:id       lid
                            :label    (if sname (str sname " - " (:label l)) (:label l))
                            :space_id sid}))
                       (when-let [cl (some #(when (= (:id %) lid-str) %) configured-lists)]
                         (let [sname (get space-names (:space_id cl))]
                           {:id       lid
                            :label    (if sname (str sname " - " (:name cl)) (:name cl))
                            :space_id (:space_id cl)}))))))
         vec)))


(defn clickup-entity-mapping-form []
  (let [loading?              @(re-frame/subscribe [::subs/loading?])
        requirements-enabled? @(re-frame/subscribe [::subs/requirements-sync-enabled?])
        issues-enabled?       @(re-frame/subscribe [::subs/issues-sync-enabled?])
        configured-lists      @(re-frame/subscribe [::subs/configured-lists])
        entity-mapping        @(re-frame/subscribe [::subs/data])
        current-mappings      (:project_mappings entity-mapping)
        allow-issue-creation? (:allow_issue_creation entity-mapping false)
        use-pt-id?            (:use_pt_id entity-mapping false)
        skip-mapping?         (:skip_mapping_in_manual_import entity-mapping false)
        integration-name      @(re-frame/subscribe [::subs/integration-name])
        available-spaces      @(re-frame/subscribe [::subs/available-spaces])
        spaces-loading?       @(re-frame/subscribe [::subs/spaces-loading?])
        selected-space-ids    @(re-frame/subscribe [::subs/selected-space-ids])
        available-lists-by-space @(re-frame/subscribe [::subs/available-lists-by-space])
        lists-loading?        @(re-frame/subscribe [::subs/lists-loading?])
        selected-list-ids     @(re-frame/subscribe [::subs/selected-project-ids])
        projects-for-section  (build-projects-for-section selected-list-ids
                                                          available-lists-by-space
                                                          configured-lists
                                                          available-spaces)]
    [:div.integration-form-content
     [:div.form-group.integration-env
      [:label (format-translation (get-translation "bugtracker.integration-settings.env"
                                                   "%{bugtracker} Environment")
                                  :bugtracker "ClickUp") ":"]
      [:input.form-control.project-name-input {:type     "text"
                                               :value    (str integration-name)
                                               :disabled true}]]
     (if loading?
       [:div.loading-container
        [spinners/mux-spinner {:size :medium}]]
       [:<>
        [space-list-selector
         {:available-spaces         (mapv (fn [s] {:id (:id s) :name (:name s)}) available-spaces)
          :selected-space-ids       selected-space-ids
          :spaces-loading?          spaces-loading?
          :available-lists-by-space available-lists-by-space
          :selected-list-ids        selected-list-ids
          :lists-loading?           lists-loading?}]
        [shared-form/synchronization-section
         {:title                  (get-translation "bugtracker.integration-settings.requirements-synchronization.title"
                                                   "Enable Requirement Synchronization")
          :enabled?               requirements-enabled?
          :other-entity-enabled?  issues-enabled?
          :on-toggle              #(re-frame/dispatch [::events/toggle-requirements-sync %])
          :projects               projects-for-section
          :current-mappings       current-mappings
          :entity-type            :requirements
          :bugtracker             "ClickUp"
          :lists-loading?         lists-loading?
          :mapping-item-component list-mapping-item}]
        [shared-form/synchronization-section
         {:title                  (get-translation "bugtracker.integration-settings.issues-synchronization.title"
                                                   "Enable Issue Synchronization")
          :enabled?               issues-enabled?
          :other-entity-enabled?  requirements-enabled?
          :on-toggle              #(re-frame/dispatch [::events/toggle-issues-sync %])
          :projects               projects-for-section
          :current-mappings       current-mappings
          :entity-type            :issues
          :bugtracker             "ClickUp"
          :lists-loading?         lists-loading?
          :mapping-item-component list-mapping-item}]
        [shared-form/general-settings-section
         {:allow-issue-creation?   allow-issue-creation?
          :use-pt-id?              use-pt-id?
          :skip-mapping?           skip-mapping?
          :bugtracker              "ClickUp"
          :on-allow-issue-creation #(re-frame/dispatch [::events/toggle-allow-issue-creation %])
          :on-use-pt-id            #(re-frame/dispatch [::events/toggle-use-pt-id %])
          :on-skip-mapping         #(re-frame/dispatch [::events/toggle-skip-mapping-manual-import %])}]
        [shared-form/form-actions
         {:saving?   @(re-frame/subscribe [::subs/saving?])
          :dirty?    @(re-frame/subscribe [::subs/dirty?])
          :on-cancel #(re-frame/dispatch [::events/cancel-changes])
          :on-save   #(re-frame/dispatch [::events/save-settings])}]])]))


(defn clickup-integration-form-root [_]
  (fn [_]
    [clickup-entity-mapping-form]))

(defn ^:export init [webcomponent options]
  (init/init! {:options       options
               :init-event-fn (fn [config]
                                [[::events/initialize-db config]
                                 [::events/fetch-entity-mapping]])
               :webcomponent  webcomponent
               :root-view     clickup-integration-form-root}))

(defn ^:dev/after-load remount []
  (init/remount-roots "clickup-integration-form-component"
                      clickup-integration-form-root))

(utils/register-webcomponent! "clickup-integration-form-component")
